﻿// Instruction builders. All accounts are passed explicitly (accountsStrict) so nothing depends on
// client-side account resolution.
import { Keypair, SystemProgram, SYSVAR_RENT_PUBKEY } from "@solana/web3.js";
import {
  program,
  BN,
  configPda,
  launchPda,
  launchIndexPda,
  curvePda,
  wellPda,
  bucketPda,
  metadataPda,
  ata,
  fetchConfig,
  METADATA_PROGRAM_ID,
  TOKEN_PROGRAM_ID,
  ASSOCIATED_TOKEN_PROGRAM_ID,
} from "./chain.js";
import { sendInstructions } from "./tx.js";
import { walletKey } from "./wallet.js";
import { TxError } from "./errors.js";
import { createAssociatedTokenAccountIdempotentInstruction } from "@solana/spl-token";

const bn = (v) => new BN(BigInt(v).toString());

/** One transaction: create mint + launch + curve + well + metadata + dev buy. */
export async function launchToken({ name, symbol, uri, devBuyLamports, devMinTokensOut, onStage }) {
  const creator = walletKey();
  if (!creator) throw new TxError("Connect your Phantom wallet first.");
  const cfg = await fetchConfig(true);
  if (!cfg) throw new TxError("The platform is not initialized on this network yet.");
  if (cfg.launchesPaused) throw new TxError("New launches are paused by the platform right now.");

  const mintKp = Keypair.generate();
  const mint = mintKp.publicKey;
  const curve = curvePda(mint);
  const well = wellPda(mint);
  const ix = await program.methods
    .initializeLaunch({
      name,
      symbol,
      uri,
      devBuyLamports: bn(devBuyLamports),
      devMinTokensOut: bn(devMinTokensOut),
    })
    .accountsStrict({
      creator,
      config: configPda(),
      treasury: cfg.treasury,
      mint,
      launch: launchPda(mint),
      launchIndex: launchIndexPda(cfg.launchCount),
      curve,
      curveVault: ata(mint, curve),
      well,
      wellVault: ata(mint, well),
      creatorAta: ata(mint, creator),
      metadata: metadataPda(mint),
      tokenMetadataProgram: METADATA_PROGRAM_ID,
      tokenProgram: TOKEN_PROGRAM_ID,
      associatedTokenProgram: ASSOCIATED_TOKEN_PROGRAM_ID,
      systemProgram: SystemProgram.programId,
      rent: SYSVAR_RENT_PUBKEY,
    })
    .instruction();
  const res = await sendInstructions({ ixs: [ix], signers: [mintKp], minCu: 400_000, onStage });
  return { ...res, mint };
}

export async function swap({ mint, side, amount, minOut, onStage }) {
  const trader = walletKey();
  if (!trader) throw new TxError("Connect your Phantom wallet first.");
  const cfg = await fetchConfig();
  if (!cfg) throw new TxError("The platform is not initialized on this network yet.");
  const curve = curvePda(mint);
  const ix = await program.methods
    .executeSwap(side === "buy" ? { buy: {} } : { sell: {} }, bn(amount), bn(minOut))
    .accountsStrict({
      trader,
      config: configPda(),
      treasury: cfg.treasury,
      mint,
      launch: launchPda(mint),
      curve,
      curveVault: ata(mint, curve),
      well: wellPda(mint),
      traderAta: ata(mint, trader),
      tokenProgram: TOKEN_PROGRAM_ID,
      associatedTokenProgram: ASSOCIATED_TOKEN_PROGRAM_ID,
      systemProgram: SystemProgram.programId,
    })
    .instruction();
  return sendInstructions({ ixs: [ix], onStage });
}

export async function stake({ mint, amount, onStage }) {
  const owner = walletKey();
  if (!owner) throw new TxError("Connect your Phantom wallet first.");
  const well = wellPda(mint);
  const ix = await program.methods
    .stakeTokens(bn(amount))
    .accountsStrict({
      owner,
      mint,
      well,
      wellVault: ata(mint, well),
      ownerAta: ata(mint, owner),
      bucket: bucketPda(mint, owner),
      tokenProgram: TOKEN_PROGRAM_ID,
      systemProgram: SystemProgram.programId,
    })
    .instruction();
  return sendInstructions({ ixs: [ix], onStage });
}

export async function unstake({ mint, amount, onStage }) {
  const owner = walletKey();
  if (!owner) throw new TxError("Connect your Phantom wallet first.");
  const well = wellPda(mint);
  const ix = await program.methods
    .unstakeTokens(bn(amount))
    .accountsStrict({
      owner,
      mint,
      well,
      wellVault: ata(mint, well),
      ownerAta: ata(mint, owner),
      bucket: bucketPda(mint, owner),
      tokenProgram: TOKEN_PROGRAM_ID,
    })
    .instruction();
  // the program expects the owner's token account to exist; recreate it if the user closed it
  const pre = createAssociatedTokenAccountIdempotentInstruction(owner, ata(mint, owner), owner, mint, TOKEN_PROGRAM_ID, ASSOCIATED_TOKEN_PROGRAM_ID);
  return sendInstructions({ ixs: [pre, ix], onStage });
}

export async function claim({ mint, onStage }) {
  const owner = walletKey();
  if (!owner) throw new TxError("Connect your Phantom wallet first.");
  const ix = await program.methods
    .claimRewards()
    .accountsStrict({ owner, mint, well: wellPda(mint), bucket: bucketPda(mint, owner) })
    .instruction();
  return sendInstructions({ ixs: [ix], onStage });
}
